import React from 'react';
import { Phone, Mail, MapPin, Clock, MessageSquare } from 'lucide-react';
import DetailedContactForm from '../components/Forms/DetailedContactForm';

const ContactPage = () => {
  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-mimo-primary to-slate-900 text-white py-20 md:py-28">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-extrabold font-heading mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Ready to transform your IT infrastructure? Get in touch with our team for a free consultation. We'll discuss your needs and show you how mimoIT can help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">
                Get In Touch
              </h2>
              
              <div className="space-y-6">
                <a 
                  href="tel:201-473-4443" 
                  className="flex items-start gap-4 group"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 rounded-xl bg-mimo-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-mimo-primary transition-colors">
                    <Phone className="w-6 h-6 text-mimo-primary group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Phone</h3>
                    <p className="text-mimo-primary font-medium">201-473-4443</p>
                    <p className="text-sm text-slate-500">24/7 Emergency Hotline</p>
                  </div>
                </a>
                
                <a 
                  href="mailto:info@mimoit.com" 
                  className="flex items-start gap-4 group"
                  data-testid="contact-info-email"
                >
                  <div className="w-12 h-12 rounded-xl bg-mimo-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-mimo-primary transition-colors">
                    <Mail className="w-6 h-6 text-mimo-primary group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">General Inquiries</h3>
                    <p className="text-mimo-primary font-medium">info@mimoit.com</p>
                    <p className="text-sm text-slate-500">Questions about our services</p>
                  </div>
                </a>
                
                <a 
                  href="mailto:service@mimoit.com" 
                  className="flex items-start gap-4 group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 rounded-xl bg-mimo-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-mimo-primary transition-colors">
                    <Mail className="w-6 h-6 text-mimo-primary group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Service Request</h3>
                    <p className="text-mimo-primary font-medium">service@mimoit.com</p>
                    <p className="text-sm text-slate-500">Open a support ticket (1-2 hour response)</p>
                  </div>
                </a>

                <a 
                  href="mailto:priority_service@mimoit.com" 
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0 group-hover:bg-red-500 transition-colors">
                    <Mail className="w-6 h-6 text-red-500 group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Priority Support</h3>
                    <p className="text-red-500 font-medium">priority_service@mimoit.com</p>
                    <p className="text-sm text-slate-500">For urgent after-hours issues</p>
                  </div>
                </a>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-mimo-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-mimo-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Support Hours</h3>
                    <p className="text-slate-600">Mon - Fri: 8:30 AM - 5:30 PM ET</p>
                    <p className="text-sm text-slate-500">24/7 emergency support available</p>
                  </div>
                </div>
              </div>
              
              {/* Locations */}
              <div className="mt-10">
                <h3 className="text-lg font-bold font-heading text-slate-900 mb-4">Our Locations</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4" data-testid="location-nj">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-mimo-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">New Jersey Office</h4>
                      <p className="text-slate-600 text-sm">31 Vose Ave, Unit 6</p>
                      <p className="text-slate-600 text-sm">South Orange, NJ 07079</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4" data-testid="location-ny">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-mimo-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">New York Office</h4>
                      <p className="text-slate-600 text-sm">450 Lexington Ave #3101</p>
                      <p className="text-slate-600 text-sm">New York, NY 10163</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Service Areas */}
              <div className="mt-10 p-6 bg-slate-50 rounded-xl">
                <h3 className="font-bold text-slate-900 mb-3">Service Areas</h3>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-mimo-secondary" />
                    New Jersey (On-site & Remote)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-mimo-secondary" />
                    New York (On-site & Remote)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-mimo-secondary" />
                    Nationwide (Remote Management)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-mimo-secondary" />
                    International (Remote Support)
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <MessageSquare className="w-6 h-6 text-mimo-primary" />
                  <h2 className="text-2xl font-bold font-heading text-slate-900">
                    Request a Consultation
                  </h2>
                </div>
                <p className="text-slate-600 mb-8">
                  Fill out the form below and one of our IT specialists will contact you within one business day to discuss your needs.
                </p>
                <DetailedContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-slate-100">
        <div className="container-custom py-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="p-4 border-b">
                <h3 className="font-bold text-slate-900">New Jersey Office</h3>
              </div>
              <iframe
                title="mimoIT NJ Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.9!2d-74.26!3d40.75!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDQ1JzAwLjAiTiA3NMKwMTUnMzYuMCJX!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale hover:grayscale-0 transition-all"
              />
            </div>
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="p-4 border-b">
                <h3 className="font-bold text-slate-900">New York Office</h3>
              </div>
              <iframe
                title="mimoIT NY Office Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.2!2d-73.97!3d40.75!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDQ1JzAwLjAiTiA3M8KwNTgnMTIuMCJX!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale hover:grayscale-0 transition-all"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;